import { useEffect } from "react";
import { trackEvent } from "@/lib/analytics";

const LAST_DATE_KEY = "mmntm:last_active_date";
const STREAK_KEY = "mmntm:session_streak";

/**
 * Silent once-per-day activity + streak tracker.
 *
 * Mounted once in the authenticated shell; fires two PostHog events
 * keyed off the user's local calendar day:
 *
 *   • `daily_active_session` — on the first app load of any new local
 *     day. Gives us true DAU without having to reverse-engineer from
 *     pageviews.
 *
 *   • `session_streak_started` — when the new-day visit follows
 *     yesterday's visit (consecutive-day streak ≥ 2). Payload includes
 *     `{ streak_length }` so the PostHog retention curve can bucket by
 *     actual streak depth.
 *
 * State lives entirely in localStorage; no backend writes. A single
 * user opening the app multiple times in one day only produces one
 * event.
 */
function todayISO() {
  // Use local-date components so "one day" matches the user's wall
  // clock, not UTC (otherwise a user in UTC-8 rolling over at local
  // midnight would have their event fire 8hrs early by UTC standards).
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function daysBetween(fromISO, toISO) {
  // Parse as local dates; difference in whole days. 0 = same day,
  // 1 = consecutive, >1 = skipped days.
  const [y1, m1, d1] = fromISO.split("-").map(Number);
  const [y2, m2, d2] = toISO.split("-").map(Number);
  const from = new Date(y1, m1 - 1, d1).getTime();
  const to = new Date(y2, m2 - 1, d2).getTime();
  return Math.round((to - from) / 86_400_000);
}

export default function DailyActiveTracker() {
  useEffect(() => {
    let last, streak;
    try {
      last = localStorage.getItem(LAST_DATE_KEY);
      streak = parseInt(localStorage.getItem(STREAK_KEY) || "0", 10) || 0;
    } catch {
      return; // localStorage unavailable — skip silently.
    }

    const today = todayISO();
    if (last === today) return; // Already counted today.

    // Compute the new streak. A visit today + last visit yesterday
    // extends the streak; otherwise the streak resets to 1.
    const gap = last ? daysBetween(last, today) : null;
    const newStreak = gap === 1 ? streak + 1 : 1;

    try {
      localStorage.setItem(LAST_DATE_KEY, today);
      localStorage.setItem(STREAK_KEY, String(newStreak));
    } catch { /* localStorage unavailable — streak simply isn't persisted this session */ }

    trackEvent("daily_active_session", {
      date: today,
      previous_visit: last || null,
      gap_days: gap,
      streak_length: newStreak,
    });

    // Dormant-return churn signal — fires whenever a user comes back
    // after 7+ days away. Not gated by a one-shot flag because the
    // gap_days check is inherently self-gating: it only triggers on
    // the first new-day visit after dormancy, and a user who goes
    // dormant again will naturally re-qualify next time.
    if (gap !== null && gap >= 7) {
      trackEvent("dormant_user_returned", {
        days_away: gap,
        previous_visit: last,
        returned_on: today,
      });
    }

    // Only fire the streak event on day ≥ 2 — a single day isn't a
    // streak. Gated implicitly by the `last === today` early return
    // above, so each consecutive day fires exactly one streak event.
    if (newStreak >= 2) {
      trackEvent("session_streak_started", { streak_length: newStreak });
    }
  }, []);

  return null;
}
